import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { 
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from '@/components/ui/button';
import { Bell, DollarSign } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { zhCN, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

interface RefundRequest {
  id: string;
  client_id: string;
  refund_reason: string;
  refund_requested_at: string;
  start_time: string;
  service_name: string;
  client_name: string;
  price_at_booking: number;
  currency_at_booking: string;
}

interface RefundNotificationSystemProps {
  onNavigateToRefund: (bookingId: string) => void;
}

export const RefundNotificationSystem: React.FC<RefundNotificationSystemProps> = ({
  onNavigateToRefund
}) => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation('dashboard');
  const [pendingRefunds, setPendingRefunds] = useState<RefundRequest[]>([]);
  const [showNotification, setShowNotification] = useState(false);
  const [currentRefund, setCurrentRefund] = useState<RefundRequest | null>(null);
  
  const locale = i18n.language === 'zh' ? zhCN : enUS;

  useEffect(() => {
    if (!user?.id) return;

    // Fetch existing pending refunds
    const fetchPendingRefunds = async () => {
      console.log('🔍 Fetching pending refunds for practitioner:', user.id);
      
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          id,
          client_id,
          refund_reason,
          refund_requested_at,
          start_time,
          price_at_booking,
          currency_at_booking,
          services!inner(service_name),
          clients!inner(first_name, last_name)
        `)
        .eq('practitioner_id', user.id)
        .eq('refund_status', 'requested')
        .order('refund_requested_at', { ascending: false });

      if (error) {
        console.error('Error fetching pending refunds:', error);
        return;
      }

      if (data) {
        const formattedRefunds = data.map(booking => ({
          id: booking.id,
          client_id: booking.client_id,
          refund_reason: booking.refund_reason || '',
          refund_requested_at: booking.refund_requested_at || '',
          start_time: booking.start_time,
          service_name: booking.services?.service_name || '',
          client_name: `${booking.clients?.first_name || ''} ${booking.clients?.last_name || ''}`.trim(),
          price_at_booking: booking.price_at_booking || 0,
          currency_at_booking: booking.currency_at_booking || 'USD'
        }));
        
        console.log('📋 Found pending refunds:', formattedRefunds.length);
        setPendingRefunds(formattedRefunds);
      }
    };

    fetchPendingRefunds();

    // Set up realtime subscription for new refund requests
    console.log('🔴 Setting up realtime subscription for refund requests');
    
    const channel = supabase
      .channel('refund-notifications')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'bookings',
          filter: `practitioner_id=eq.${user.id}`,
        },
        async (payload) => {
          console.log('📨 Realtime update received:', payload);
          
          const newBooking = payload.new;
          const oldBooking = payload.old;
          
          // Check if refund_status changed to 'requested'
          if (newBooking.refund_status === 'requested' && 
              oldBooking.refund_status !== 'requested') {
            
            console.log('🚨 New refund request detected!', newBooking.id);
            
            // Fetch additional details for the notification
            const { data: bookingDetails, error } = await supabase
              .from('bookings')
              .select(`
                id,
                client_id,
                refund_reason,
                refund_requested_at,
                start_time,
                price_at_booking,
                currency_at_booking,
                services!inner(service_name),
                clients!inner(first_name, last_name)
              `)
              .eq('id', newBooking.id)
              .single();

            if (!error && bookingDetails) {
              const refundRequest: RefundRequest = {
                id: bookingDetails.id,
                client_id: bookingDetails.client_id,
                refund_reason: bookingDetails.refund_reason || '',
                refund_requested_at: bookingDetails.refund_requested_at || '',
                start_time: bookingDetails.start_time,
                service_name: bookingDetails.services?.service_name || '',
                client_name: `${bookingDetails.clients?.first_name || ''} ${bookingDetails.clients?.last_name || ''}`.trim(),
                price_at_booking: bookingDetails.price_at_booking || 0,
                currency_at_booking: bookingDetails.currency_at_booking || 'USD'
              };

              setPendingRefunds(prev => [refundRequest, ...prev]);
              setCurrentRefund(refundRequest);
              setShowNotification(true);
              
              // Play notification sound (optional)
              try {
                const audio = new Audio('/notification.mp3');
                audio.volume = 0.5;
                audio.play().catch(() => {
                  // Silently fail if audio can't play (no autoplay permission)
                });
              } catch (e) {
                // Ignore audio errors
              }
            }
          }
          
          // Remove from pending if status changed away from 'requested'
          if (oldBooking.refund_status === 'requested' && 
              newBooking.refund_status !== 'requested') {
            console.log('✅ Refund request resolved:', newBooking.id);
            setPendingRefunds(prev => prev.filter(r => r.id !== newBooking.id));
          }
        }
      )
      .subscribe();

    return () => {
      console.log('🔌 Cleaning up refund notification subscription');
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const handleViewRefund = () => {
    if (currentRefund) {
      setShowNotification(false);
      onNavigateToRefund(currentRefund.id);
    }
  };

  const handleDismiss = () => {
    setShowNotification(false);
  };

  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  };

  const formatTimeAgo = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { 
        addSuffix: true,
        locale 
      });
    } catch {
      return dateString;
    }
  };

  return (
    <>
      {/* Notification Badge - can be used elsewhere to show pending count */}
      {pendingRefunds.length > 0 && (
        <div className="fixed top-20 right-4 z-50">
          <div className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2">
            <Bell className="h-4 w-4" />
            {pendingRefunds.length} {t('refunds.pendingRequests', 'Pending Refund Requests')}
          </div>
        </div>
      )}

      {/* Pop-up Notification Dialog */}
      <AlertDialog open={showNotification} onOpenChange={setShowNotification}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <DollarSign className="h-5 w-5" />
              {t('refunds.newRequest', 'New Refund Request')}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              {currentRefund && (
                <>
                  <div className="bg-amber-50 p-3 rounded-md border">
                    <p className="font-medium text-gray-900">
                      {t('refunds.from', 'From')}: {currentRefund.client_name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {t('refunds.service', 'Service')}: {currentRefund.service_name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {t('refunds.amount', 'Amount')}: {formatPrice(currentRefund.price_at_booking, currentRefund.currency_at_booking)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {t('refunds.requested', 'Requested')}: {formatTimeAgo(currentRefund.refund_requested_at)}
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-3 rounded-md">
                    <p className="text-sm font-medium text-gray-900 mb-1">
                      {t('refunds.reason', 'Reason')}:
                    </p>
                    <p className="text-sm text-gray-700">
                      {currentRefund.refund_reason || t('refunds.noReason', 'No reason provided')}
                    </p>
                  </div>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={handleDismiss}
              className="flex-1"
            >
              {t('common.dismiss', 'Dismiss')}
            </Button>
            <Button 
              onClick={handleViewRefund}
              className="flex-1 bg-amber-600 hover:bg-amber-700"
            >
              {t('refunds.viewRequest', 'View Request')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};